import { useCallback, useState } from 'react';
import type { RefCallback } from 'react';

import { link, params } from '@aline/routes';
import { getScrollParent } from 'src/helpers/dom';
import { useCurrentTabId, useIsTabActive } from 'src/layouts/tabs/tab-active-context';
import { proxy, useSnapshot } from 'valtio';

type IntegrationsSectionSpy = ReturnType<typeof createIntegrationsSectionSpy>;

const spiesByTab = new Map<string, IntegrationsSectionSpy>();

export function getIntegrationsSectionLink(sectionId: string): string {
  const search = new URLSearchParams({ [params.integrationsSection]: sectionId });
  return `${link.AdminIntegrations()}?${search}`;
}

export function useActiveIntegrationsSection(): string | null {
  const tabId = useCurrentTabId();
  return useSnapshot(getTabSpy(tabId).active).id;
}

export function useIntegrationsSectionNav() {
  const tabId = useCurrentTabId();

  return {
    scrollToSection: useCallback(
      (sectionId: string) => scrollSectionIntoTab(sectionId, tabId),
      [tabId]
    ),
    selectSection: useCallback((sectionId: string) => getTabSpy(tabId).select(sectionId), [tabId]),
  };
}

export function useIntegrationsScrollRoot() {
  const isSpyActive = useIsTabActive();
  const tabId = useCurrentTabId();
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  const listRef = useCallback(
    (node: HTMLDivElement | null) => {
      const root = node ? getScrollParent(node) : null;
      setScrollRoot(root);
      const cleanup = getTabSpy(tabId).setScrollRoot(root);
      if (!root) return;

      return () => {
        cleanup();
        setScrollRoot(null);
        spiesByTab.delete(tabId ?? 'default');
      };
    },
    [tabId]
  );

  return { isSpyActive, listRef, scrollRoot };
}

export function useIntegrationsSectionRef(options: {
  enabled: boolean;
  id: string;
  root: HTMLElement | null;
}): RefCallback<HTMLElement> {
  const { enabled, id, root } = options;
  const tabId = useCurrentTabId();

  return useCallback(
    (element: HTMLElement | null) => {
      getTabSpy(tabId).observe(id, enabled && root ? element : null);
    },
    [enabled, id, root, tabId]
  );
}

function createIntegrationsSectionSpy() {
  const active = proxy<{ id: string | null }>({ id: null });
  const sections = new Map<string, HTMLElement>();
  let scrollRoot: HTMLElement | null = null;
  let pinnedId: string | null = null;
  let arbitrationFrame = 0;

  /**
   * Sections keep growing for a while after the first paint: connection states arrive and each
   * file-storage row gains its sync jobs, which pushes every section below them further down.
   * Growth above a pinned section leaves it off the reading line without emitting a scroll
   * event, so the resize is what tells us to move back to it.
   */
  const layoutObserver = new ResizeObserver(() => {
    if (pinnedId) followPinnedSection();
    else scheduleArbitration();
  });

  function scheduleArbitration() {
    if (arbitrationFrame) return;
    arbitrationFrame = window.requestAnimationFrame(() => {
      arbitrationFrame = 0;
      const activeId = getActiveSectionId(sections, scrollRoot);
      if (!activeId) return;
      // A pinned section keeps the highlight while our own scroll towards it is settling.
      if (pinnedId && activeId !== pinnedId) return;
      active.id = activeId;
    });
  }

  /**
   * Scrolls the pinned section back to the reading line. A section that is already there and
   * still loses arbitration can never win it, because the page will not scroll any further, so
   * the pin is dropped instead of holding a highlight the page contradicts.
   */
  function followPinnedSection() {
    const target = pinnedId ? sections.get(pinnedId) : null;
    if (target && scrollElementIntoSectionView(target)) return;
    if (getActiveSectionId(sections, scrollRoot) !== pinnedId) pinnedId = null;
    scheduleArbitration();
  }

  return {
    active,
    observe(id: string, element: HTMLElement | null) {
      const observed = sections.get(id);
      if (observed) layoutObserver.unobserve(observed);

      if (element) {
        sections.set(id, element);
        layoutObserver.observe(element);
      } else {
        sections.delete(id);
      }

      scheduleArbitration();
    },
    select(sectionId: string) {
      active.id = sectionId;
      pinnedId = sectionId;
    },
    setScrollRoot(root: HTMLElement | null) {
      scrollRoot = root;
      if (!root) return () => {};

      /**
       * Only the user's own scrolling releases a pin. Releasing on `scrollend` instead would
       * hand the highlight over the instant our scroll landed, while the sections above the
       * target are still growing. `pointerdown` covers dragging the scrollbar, which emits
       * neither a wheel nor a touch event.
       */
      const releaseEvents = ['wheel', 'touchmove', 'keydown', 'pointerdown'];
      const onRelease = () => {
        if (!pinnedId) return;
        pinnedId = null;
        scheduleArbitration();
      };

      root.addEventListener('scroll', scheduleArbitration, { passive: true });
      for (const event of releaseEvents) root.addEventListener(event, onRelease, { passive: true });
      layoutObserver.observe(root);
      scheduleArbitration();

      return () => {
        root.removeEventListener('scroll', scheduleArbitration);
        for (const event of releaseEvents) root.removeEventListener(event, onRelease);
        layoutObserver.disconnect();
        if (scrollRoot !== root) return;
        window.cancelAnimationFrame(arbitrationFrame);
        arbitrationFrame = 0;
        scrollRoot = null;
      };
    },
  };
}

function getTabSpy(tabId: string | null): IntegrationsSectionSpy {
  const key = tabId ?? 'default';
  const existing = spiesByTab.get(key);
  if (existing) return existing;

  const spy = createIntegrationsSectionSpy();
  spiesByTab.set(key, spy);
  return spy;
}

function scrollSectionIntoTab(sectionId: string, tabId: string | null): void {
  let attempts = 0;

  function tryScroll() {
    const element = findSectionElement(sectionId, tabId);
    if (element && element.getBoundingClientRect().height > 0) {
      scrollElementIntoSectionView(element);
      return;
    }
    if (attempts >= 90) return;
    attempts += 1;
    requestAnimationFrame(tryScroll);
  }

  requestAnimationFrame(tryScroll);
}

function findSectionElement(sectionId: string, tabId: string | null): HTMLElement | null {
  const selector = tabId ? `[data-tab-id="${tabId}"] [id="${sectionId}"]` : `[id="${sectionId}"]`;
  const element = document.querySelector(selector);
  if (element instanceof HTMLElement) return element;
  return null;
}

function scrollElementIntoSectionView(element: HTMLElement): boolean {
  const scrollParent = getScrollParent(element);
  if (!scrollParent) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  const parentRect = scrollParent.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const scrollMarginTop = Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
  const top = scrollParent.scrollTop + (elementRect.top - parentRect.top) - scrollMarginTop;
  if (Math.abs(top - scrollParent.scrollTop) < 4) return false;

  scrollParent.scrollTo({ behavior: 'smooth', top });
  return true;
}

function getActiveSectionId(
  sections: ReadonlyMap<string, HTMLElement>,
  scrollRoot: HTMLElement | null
): string | null {
  const ordered = [...sections]
    .map(([id, element]) => ({ id, top: element.getBoundingClientRect().top }))
    .sort((a, b) => a.top - b.top);

  // At the very end of a scrollable page the last section wins even if it never reaches the
  // reading line. A page that does not scroll must not pin the last section from first paint.
  if (scrollRoot) {
    const { clientHeight, scrollHeight, scrollTop } = scrollRoot;
    const isScrollable = scrollHeight > clientHeight;
    if (isScrollable && scrollTop + clientHeight >= scrollHeight - 4)
      return ordered.at(-1)?.id ?? null;
  }

  const readingLine = (scrollRoot?.getBoundingClientRect().top ?? 0) + 48;
  const crossed = ordered.filter(section => section.top <= readingLine);
  return (crossed.at(-1) ?? ordered.at(0))?.id ?? null;
}
